// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Segment } from "semantic-ui-react";
import PropTypes from "prop-types";

import _ from "lodash";

import AFPlot from "../../../Lumber/Plotting/AFPlotly";
import { BRAND_COLOR } from "../../../../utilities/graphics";

const BAR_COLOR = "rgba(7, 121, 182, 1)"; //--accent-alternate-UI-600
const GRID_COLOR = "rgba(227, 228, 223, 1)"; //--neutral-100
const BASE_LAYOUT_LOCATION_COUNT = 6;
const MIN_CHART_HEIGHT = 360;
const LOCATION_ROW_HEIGHT = 28;

const PianoChart = ({
	selectedAverageTreatments,
	siteData,
	trialTreatmentId,
	treatmentName,
	minimumAverage,
	maximumAverage,
	needToUpdateAnalytics,
	layoutLocationCount,
	featureName,
}) => {
	//-- Data
	const [chartOptions, setChartOptions] = useState({ data: [], layout: {} });
	const [chartHeight, setChartHeight] = useState(MIN_CHART_HEIGHT);
	const [chartHeader, setChartHeader] = useState({ title: "", winRate: "" });

	//-- UI Control
	const [chartLoading, setChartLoading] = useState(true);

	useEffect(() => {
		if (siteData && selectedAverageTreatments) {
			stageChartOptions();
		}
	}, [siteData, selectedAverageTreatments]);

	function calculateDataPoints() {
		let dataPoints = _.map(siteData, (sd) => {
			const value = Number(sd.average);
			const location = [sd.city, sd.region, sd.countryCode].filter(Boolean).join(", ");
			return {
				value: Number.isFinite(value) ? value : 0,
				label: location || "Unknown Location",
				trialName: sd.trialName,
				color: BAR_COLOR,
			};
		});

		dataPoints = _.orderBy(dataPoints, ["value"]);
		return !dataPoints || dataPoints?.length === 0 ? [] : dataPoints;
	}

	function calculateWinRate(dataPoints) {
		let numberOfWins = _.countBy(dataPoints, (dp) => {
			return dp.value > 0;
		});
		if (!numberOfWins.true || numberOfWins.true === 0) {
			return 0;
		}
		let winRate = Number((numberOfWins.true / dataPoints.length) * 100);
		return winRate % 1 === 0 ? winRate : winRate.toFixed(2);
	}

	function formatPercent(value) {
		return value % 1 === 0 ? `${value}%` : `${value.toFixed(2)}%`;
	}

	function getAxisRange(dataPoints) {
		const values = dataPoints.map((dp) => dp.value);
		const rangeValues = [minimumAverage, maximumAverage].map(Number).filter(Number.isFinite);
		const min = rangeValues.length > 0 ? Math.min(...rangeValues) : Math.min(...values);
		const max = rangeValues.length > 0 ? Math.max(...rangeValues) : Math.max(...values);

		if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;

		const span = max - min;
		const pad = span === 0 ? Math.abs(max) * 0.12 || 1 : span * 0.14;
		return [min - pad, max + pad];
	}

	function getBarBase(dataPoints, axisRange) {
		if (!axisRange) return undefined;

		const values = dataPoints.map((dp) => dp.value);
		const min = Math.min(...values);
		const max = Math.max(...values);

		if (min > 0 && axisRange[0] > 0) return axisRange[0];
		if (max < 0 && axisRange[1] < 0) return axisRange[1];
		return undefined;
	}

	function getSnapshotFileName(subTitleName) {
		const assessmentName = siteData?.[0]?.assessmentName;
		return [featureName, assessmentName, subTitleName, "PianoChart"]
			.filter(Boolean)
			.join("_")
			.replace(/[\\/:*?"<>|]+/g, "")
			.replace(/\s+/g, "");
	}

	function stageChartOptions() {
		let data = calculateDataPoints();
		if (data.length === 0) {
			setChartOptions({ data: [], layout: {} });
			setChartHeight(MIN_CHART_HEIGHT);
			setChartHeader({ title: "", winRate: "" });
			setChartLoading(false);
			return;
		}

		let winRate = calculateWinRate(data);
		let subTitleName = treatmentName ? `${trialTreatmentId} - ${treatmentName}` : trialTreatmentId;
		setChartHeader({ title: `${subTitleName}`, winRate: `${winRate}%` });
		const chartLocationCount = Math.max(layoutLocationCount ?? 0, data.length, BASE_LAYOUT_LOCATION_COUNT);
		const labels = data.map((dp) => dp.label);
		const values = data.map((dp) => dp.value);
		const text = values.map(formatPercent);
		const axisRange = getAxisRange(data);
		const barBase = getBarBase(data, axisRange);
		const barTrace = {
			type: "bar",
			orientation: "h",
			x: values,
			y: labels,
			text,
			customdata: text,
			textposition: "outside",
			constraintext: "outside",
			cliponaxis: false,
			marker: {
				color: BAR_COLOR,
				line: { color: BRAND_COLOR, width: 0.5 },
			},
			hovertemplate: "<b>%{y}</b><br><b>% Difference: </b>%{customdata}<extra></extra>",
			showlegend: false,
			...(barBase === undefined ? {} : { base: barBase }),
		};

		let opt = {
			data: [barTrace],
			layout: {
				paper_bgcolor: "#fff0",
				plot_bgcolor: "#fff0",
				autosize: true,
				showlegend: false,
				bargap: chartLocationCount > 12 ? 0.16 : 0.24,
				hovermode: "closest",
				uniformtext: { mode: "hide", minsize: 10 },
				margin: { l: 138, r: 46, t: 12, b: 54 },
				xaxis: {
					title: { text: "% Difference" },
					range: axisRange,
					gridcolor: GRID_COLOR,
					zeroline: !!axisRange && axisRange[0] <= 0 && axisRange[1] >= 0,
					zerolinecolor: BRAND_COLOR,
					automargin: true,
				},
				yaxis: {
					type: "category",
					categoryorder: "array",
					categoryarray: labels,
					tickfont: { size: chartLocationCount > 14 ? 10 : 11 },
					automargin: true,
				},
			},
			config: {
				toImageButtonOptions: {
					filename: getSnapshotFileName(subTitleName),
				},
			},
		};

		setChartHeight(Math.max(MIN_CHART_HEIGHT, chartLocationCount * LOCATION_ROW_HEIGHT + 150));
		setChartOptions(opt);
		setChartLoading(false);
	}
	return (
		<Segment
			basic
			className="pianoChartSegment"
			loading={chartLoading && !needToUpdateAnalytics}
			style={{ borderRadius: "8px", border: "1px solid var(--border)", background: "#ffffffe6" }}
		>
			{chartOptions.data.length === 0 ? (
				<p>There is no data selected for the piano chart.</p>
			) : (
				<>
					<div className="pianoChartHeader">
						<div className="pianoChartTitle" title={chartHeader.title}>
							{chartHeader.title}
						</div>
						<div className="pianoChartWinRate">{chartHeader.winRate}</div>
					</div>
					<AFPlot
						data={chartOptions.data}
						layout={chartOptions.layout}
						config={chartOptions.config}
						useResizeHandler
						style={{ width: "100%", height: `${chartHeight}px` }}
					/>
				</>
			)}
		</Segment>
	);
};

PianoChart.propTypes = {
	selectedAverageTreatments: PropTypes.array,
	siteData: PropTypes.array,
	trialTreatmentId: PropTypes.number,
	treatmentName: PropTypes.string,
	minimumAverage: PropTypes.number,
	maximumAverage: PropTypes.number,
	needToUpdateAnalytics: PropTypes.bool,
	layoutLocationCount: PropTypes.number,
	featureName: PropTypes.string,
};

export default PianoChart;
